// filepath: c:\Users\Salim\Documents\Salim\Personal\Projects\K & K\CFMS\project-root\frontend\src\components\admin\Departments.js
import React, { useEffect, useState, useCallback } from 'react';
import Select2Dropdown from '../common/Select2Dropdown';
import useSortableTable from '../common/useSortableTable';
import { MultiSelectDropdown } from '../common/MultiSelectDropdown';
import { toast } from 'react-toastify';
import { APP_MODULES_HIERARCHICAL } from '../../appConfig'; // Using the hierarchical list
import { usePermissions } from '../../services/PermissionsContext'; // 1. Import

const API_BASE = '/api/admin/departments/';
const PERM_API_BASE = '/api/admin/department-permissions/';
const API_PAYABLE_PERMISSIONS = '/api/admin/payable-stage-permissions/';
const API_PAYABLE_USER_PERMISSIONS = '/api/admin/payable-user-permissions/';
const API_PAYABLE_NOTIFICATION_CONFIG = '/api/admin/payable-notification-config/';
// Fetch ALL active users, then filter for STAFF client-side to ensure inheritance doesn't hide valid approvers
const API_PAYABLE_USERS = '/api/admin/users/?active=true';

const STAGES = [
    { id: 1, name: 'Stage 1' },
    { id: 2, name: 'Stage 2' },
    { id: 3, name: 'Stage 3' }
];

const authHeaders = (json = true) => {
  const token = localStorage.getItem('kk_token') || localStorage.getItem('token');
  return {
    ...(json ? { 'Content-Type': 'application/json' } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };
};

const PERM_COLS = [
    { key: 'can_view_payables',   label: 'Payables View' },
    { key: 'can_approve_payables', label: 'Payables Approve' },
    { key: 'can_view_cash',       label: 'Cash View' },
    { key: 'can_approve_cash',    label: 'Cash Approve' },
];

const PayablesConfigTab = ({ hasPermission }) => {
    const canEdit = hasPermission('PAYABLES CONFIG', 'can_edit') || hasPermission('ACCOUNTS CONFIG', 'can_edit');

    const [userOptions, setUserOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    // stageRows: { [stageId]: [ { user, user_full_name, can_view_payables, can_approve_payables, can_view_cash, can_approve_cash } ] }
    const [stageRows, setStageRows] = useState({ 1: [], 2: [], 3: [] });
    // selected user to add per stage
    const [addUser, setAddUser] = useState({ 1: '', 2: '', 3: '' });
    const [intervalMinutes, setIntervalMinutes] = useState(15);
    const [intervalId, setIntervalId] = useState(null);
    const [intervalSaving, setIntervalSaving] = useState(false);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [userRes, permRes] = await Promise.all([
                fetch(API_PAYABLE_USERS, { headers: authHeaders(false) }),
                fetch(API_PAYABLE_USER_PERMISSIONS, { headers: authHeaders(false) }),
            ]);
            if (!userRes.ok) throw new Error('Failed to load users');
            if (!permRes.ok) throw new Error('Failed to load permissions');

            const userData = await userRes.json();
            const permData = await permRes.json();

            const staffUsers = userData.filter(u => u.profile && u.profile.user_type === 'STAFF');
            setUserOptions(staffUsers.map(u => ({
                id: u.id,
                text: `${u.first_name} ${u.last_name}`.trim() || u.username,
            })));

            const rows = { 1: [], 2: [], 3: [] };
            permData.forEach(p => {
                if (rows[p.stage]) rows[p.stage].push({ ...p });
            });
            setStageRows(rows);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadData(); }, [loadData]);

    const loadInterval = useCallback(async () => {
        try {
            const res = await fetch(API_PAYABLE_NOTIFICATION_CONFIG, { headers: authHeaders(false) });
            if (res.ok) {
                const data = await res.json();
                const minutes = data.interval_minutes;
                if (minutes !== undefined && minutes !== null) {
                    setIntervalMinutes(minutes);
                    setIntervalId(data.id || null);
                }
            }
        } catch (e) {
            // ignore
        }
    }, []);

    useEffect(() => { loadInterval(); }, [loadInterval]);

    const handleSaveInterval = async () => {
        if (!canEdit) { toast.error('Permission denied'); return; }
        setIntervalSaving(true);
        try {
            const method = intervalId ? 'PUT' : 'POST';
            const url = intervalId ? `${API_PAYABLE_NOTIFICATION_CONFIG}${intervalId}/` : API_PAYABLE_NOTIFICATION_CONFIG;
            const res = await fetch(url, {
                method,
                headers: authHeaders(),
                body: JSON.stringify({ interval_minutes: parseInt(intervalMinutes, 10) || 0 }),
            });
            if (!res.ok) throw new Error('Failed to save notification interval');
            const data = await res.json();
            setIntervalId(data.id);
            toast.success('Notification interval saved');
        } catch (err) {
            toast.error(err.message);
        } finally {
            setIntervalSaving(false);
        }
    };

    const handleToggle = (stageId, userId, permKey) => {
        setStageRows(prev => ({
            ...prev,
            [stageId]: prev[stageId].map(row =>
                row.user === userId ? { ...row, [permKey]: !row[permKey] } : row
            ),
        }));
    };

    const handleAddUser = (stageId) => {
        const userId = addUser[stageId];
        if (!userId) return;
        if (stageRows[stageId].some(r => r.user === userId)) {
            toast.warn('User already added to this stage.');
            return;
        }
        const userObj = userOptions.find(u => u.id === userId || u.id === parseInt(userId));
        setStageRows(prev => ({
            ...prev,
            [stageId]: [...prev[stageId], {
                user: parseInt(userId),
                user_full_name: userObj ? userObj.text : '',
                can_view_payables: true,
                can_approve_payables: true,
                can_view_cash: false,
                can_approve_cash: false,
            }],
        }));
        setAddUser(prev => ({ ...prev, [stageId]: '' }));
    };

    const handleRemoveUser = (stageId, userId) => {
        setStageRows(prev => ({
            ...prev,
            [stageId]: prev[stageId].filter(r => r.user !== userId),
        }));
    };

    const handleSave = async (stageId) => {
        if (!canEdit) { toast.error('Permission denied'); return; }
        setSaving(true);
        try {
            const entries = stageRows[stageId].map(r => ({
                user: r.user,
                can_view_payables: r.can_view_payables,
                can_approve_payables: r.can_approve_payables,
                can_view_cash: r.can_view_cash,
                can_approve_cash: r.can_approve_cash,
            }));
            const res = await fetch(`${API_PAYABLE_USER_PERMISSIONS}bulk-save/`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({ stage: stageId, entries }),
            });
            if (!res.ok) throw new Error(`Failed to save Stage ${stageId}`);
            toast.success(`Stage ${stageId} permissions saved`);
            await loadData();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="card-custom" style={{ marginTop: '20px' }}>
            <div className="card-header-custom">
                <h3>Payables Approval Workflow</h3>
            </div>
            <div className="card-body-custom" style={{ padding: 20 }}>
                {loading && <p>Loading...</p>}
                {!loading && STAGES.map(stage => (
                    <div key={stage.id} style={{ marginBottom: 32, borderBottom: '1px solid #eee', paddingBottom: 24 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                            <strong style={{ fontSize: '1.05em' }}>{stage.name} Permissions</strong>
                            {canEdit && (
                                <button
                                    className="btn btn-primary btn-sm"
                                    onClick={() => handleSave(stage.id)}
                                    disabled={saving}
                                    style={{ minWidth: 100 }}
                                >
                                    {saving ? 'Saving…' : 'Save Stage ' + stage.id}
                                </button>
                            )}
                        </div>

                        {/* Add user row */}
                        {canEdit && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                                <div style={{ minWidth: 280 }}>
                                    <Select2Dropdown
                                        id={`add-user-stage-${stage.id}`}
                                        options={userOptions.filter(u => !stageRows[stage.id].some(r => r.user === u.id))}
                                        value={addUser[stage.id]}
                                        onChange={val => setAddUser(prev => ({ ...prev, [stage.id]: val }))}
                                        valueKey="id"
                                        displayKey="text"
                                        placeholder="Add user…"
                                    />
                                </div>
                                <button
                                    className="btn btn-secondary btn-sm"
                                    onClick={() => handleAddUser(stage.id)}
                                    disabled={!addUser[stage.id]}
                                >
                                    <i className="fa fa-plus" /> Add
                                </button>
                            </div>
                        )}

                        {stageRows[stage.id].length === 0 ? (
                            <p style={{ color: '#888', fontSize: '0.9em' }}>No users assigned to this stage.</p>
                        ) : (
                            <table className="list-table" style={{ width: '50%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr>
                                        <th style={{ width: '35%', border: '1px solid #dee2e6' }}>User</th>
                                        {PERM_COLS.map(col => (
                                            <th key={col.key} style={{ textAlign: 'center', border: '1px solid #dee2e6' }}>{col.label}</th>
                                        ))}
                                        {canEdit && <th style={{ width: 60, border: '1px solid #dee2e6' }}></th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {stageRows[stage.id].map(row => (
                                        <tr key={row.user} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f5f5f5'} onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}>
                                            <td style={{ fontWeight: 500, border: '1px solid #dee2e6' }}>{row.user_full_name}</td>
                                            {PERM_COLS.map(col => (
                                                <td key={col.key} style={{ textAlign: 'center', border: '1px solid #dee2e6' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={!!row[col.key]}
                                                        disabled={!canEdit}
                                                        onChange={() => handleToggle(stage.id, row.user, col.key)}
                                                        style={{ width: 16, height: 16, cursor: canEdit ? 'pointer' : 'default' }}
                                                    />
                                                </td>
                                            ))}
                                            {canEdit && (
                                                <td style={{ textAlign: 'center', border: '1px solid #dee2e6' }}>
                                                    <button
                                                        className="btn-icon"
                                                        title="Remove"
                                                        onClick={() => handleRemoveUser(stage.id, row.user)}
                                                        style={{ color: '#dc3545' }}
                                                    >
                                                        <i className="fa fa-trash" />
                                                    </button>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                ))}

                <div style={{ marginTop: 32, paddingTop: 24, borderTop: '2px solid #3f6b32' }}>
                    <h4 style={{ marginBottom: 12 }}>Notification Interval</h4>
                    <p style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
                        Set payable notification interval.
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                        <input
                            type="number"
                            min={1}
                            className="form-control"
                            value={intervalMinutes}
                            onChange={(e) => setIntervalMinutes(e.target.value)}
                            disabled={!canEdit}
                            style={{ width: 120 }}
                        />
                        <span style={{ color: '#555' }}>minutes</span>
                        {canEdit && (
                            <button
                                className="btn btn-primary btn-sm"
                                onClick={handleSaveInterval}
                                disabled={intervalSaving}
                                style={{ minWidth: 100 }}
                            >
                                {intervalSaving ? 'Saving…' : 'Save'}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const PermissionsManager = ({ departments, hasPermission }) => { // 2. Receive hasPermission
    const [selectedDept, setSelectedDept] = useState('');
    const [permissions, setPermissions] = useState({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [expandedRows, setExpandedRows] = useState(new Set()); // 1. State for collapsible rows

    const fetchPermissions = useCallback(async () => {
        if (!selectedDept) {
            setPermissions({});
            return;
        }
        setLoading(true);
        try {
            const res = await fetch(`${PERM_API_BASE}?department=${selectedDept}`, { headers: authHeaders(false) });
            if (res.ok) {
                const data = await res.json();
                const permsMap = APP_MODULES_HIERARCHICAL.reduce((acc, moduleInfo) => {
                    // FIX: Use the unique module key instead of the title
                    const moduleKey = moduleInfo.module;
                    const existing = data.find(p => p.module === moduleKey);
                    acc[moduleKey] = existing || { module: moduleKey, can_create: false, can_view: false, can_edit: false, can_delete: false };
                    return acc;
                }, {});
                setPermissions(permsMap);
            }
        } finally {
            setLoading(false);
        }
    }, [selectedDept]);

    useEffect(() => {
        fetchPermissions();
    }, [fetchPermissions]);

    // 2. Toggle expand/collapse state for a parent row
    const toggleRow = (moduleKey) => {
        const newExpandedRows = new Set(expandedRows);
        if (newExpandedRows.has(moduleKey)) {
            newExpandedRows.delete(moduleKey);
        } else {
            newExpandedRows.add(moduleKey);
        }
        setExpandedRows(newExpandedRows);
    };

    // Toggle expand/collapse ALL parent rows
    const toggleAllRows = () => {
        const parentModules = APP_MODULES_HIERARCHICAL.filter(m => m.level === 0).map(m => m.module);
        if (expandedRows.size === parentModules.length && parentModules.length > 0) {
            // All expanded, so collapse all
            setExpandedRows(new Set());
        } else {
            // Not all expanded, so expand all
            setExpandedRows(new Set(parentModules));
        }
    };

    // 3. Enhanced handler for cascading checkbox logic
    const handlePermissionChange = (moduleKey, permission, value) => {
        const newPermissions = { ...permissions };

        // Update the clicked module first
        newPermissions[moduleKey] = { ...newPermissions[moduleKey], [permission]: value };

        // Check if the module is a parent (level 0)
        // FIX: Find index using module key
        const moduleIndex = APP_MODULES_HIERARCHICAL.findIndex(m => m.module === moduleKey);
        if (moduleIndex !== -1) {
            if (APP_MODULES_HIERARCHICAL[moduleIndex].level === 0) {
                // It's a parent, so cascade the change to its children
                for (let i = moduleIndex + 1; i < APP_MODULES_HIERARCHICAL.length; i++) {
                    const childModule = APP_MODULES_HIERARCHICAL[i];
                    if (childModule.level > 0) { // It's a child of the current parent
                        // FIX: Use child module key
                        newPermissions[childModule.module] = { ...newPermissions[childModule.module], [permission]: value };
                    } else {
                        // We've reached the next parent, so stop
                        break;
                    }
                }
            } else {
                // It's a child, so cascade the change UP to its parent
                let parentIndex = -1;
                for (let i = moduleIndex - 1; i >= 0; i--) {
                    if (APP_MODULES_HIERARCHICAL[i].level === 0) {
                        parentIndex = i;
                        break;
                    }
                }
                if (parentIndex !== -1) {
                    const parentKey = APP_MODULES_HIERARCHICAL[parentIndex].module;
                    if (value === true) {
                        newPermissions[parentKey] = { ...newPermissions[parentKey], [permission]: true };
                    } else {
                        // If value is false, check if ANY other child is still true
                        let anyChildTrue = false;
                        for (let i = parentIndex + 1; i < APP_MODULES_HIERARCHICAL.length; i++) {
                            const childModule = APP_MODULES_HIERARCHICAL[i];
                            if (childModule.level === 0) break; // Next parent
                            if (newPermissions[childModule.module] && newPermissions[childModule.module][permission]) {
                                anyChildTrue = true;
                                break;
                            }
                        }
                        if (!anyChildTrue) {
                            newPermissions[parentKey] = { ...newPermissions[parentKey], [permission]: false };
                        }
                    }
                }
            }
        }
        setPermissions(newPermissions);
    };

    const handleSave = async () => {
        // 3. Add permission check before saving
        if (!hasPermission('DEPARTMENTS', 'can_edit')) {
            toast.error("You do not have permission to save permissions.");
            return;
        }
        if (!selectedDept) return;
        setSaving(true);
        const payload = Object.values(permissions).map(p => ({ ...p, department: selectedDept }));
        try {
            const res = await fetch(`${PERM_API_BASE}bulk-update/`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify(payload)
            });
            if (res.ok) {
                toast.success('Permissions saved successfully!');
            } else {
                const errorData = await res.json();
                toast.error(`Failed to save permissions: ${JSON.stringify(errorData)}`);
            }
        } catch (error) {
            toast.error(`An error occurred: ${error.message}`);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div>
            <div className="filters-container" style={{ padding: '0 0 16px 0' }}>
                <div className="filter-item">
                    <label>SELECT DEPARTMENT</label>
                    <Select2Dropdown
                        options={departments.map(d => ({ id: d.id, text: d.name }))}
                        value={selectedDept}
                        onChange={setSelectedDept}
                        placeholder="Choose a department to manage permissions"
                        valueKey="id"
                        displayKey="text"
                    />
                </div>
            </div>

            {selectedDept && (
                <>
                    {loading ? <p>Loading permissions...</p> : (
                        <div className="table-responsive">
                            <style>{`
                                .admin-table tbody tr.hover-highlight:hover {
                                    background-color: #f0f7ff !important;
                                }
                                .admin-table tbody tr.parent-row:hover {
                                    background-color: #fff176 !important;
                                }
                            `}</style>
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <th 
                                            style={{ cursor: 'pointer', userSelect: 'none' }} 
                                            onClick={toggleAllRows}
                                            title="Click to expand/collapse all modules"
                                        >
                                            MODULE
                                            <i 
                                                className={`fa ${expandedRows.size === APP_MODULES_HIERARCHICAL.filter(m => m.level === 0).length && APP_MODULES_HIERARCHICAL.length > 0 ? 'fa-chevron-down' : 'fa-chevron-right'}`} 
                                                style={{ marginLeft: '8px', fontSize: '0.8em' }} 
                                            />
                                        </th>
                                        <th style={{ textAlign: 'center' }}>VIEW</th>
                                        <th style={{ textAlign: 'center' }}>CREATE</th>
                                        <th style={{ textAlign: 'center' }}>EDIT</th>
                                        <th style={{ textAlign: 'center' }}>DELETE</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {APP_MODULES_HIERARCHICAL.map((moduleInfo, index) => {
                                        // FIX: Use unique module key
                                        const moduleKey = moduleInfo.module;
                                        const displayTitle = moduleInfo.title;
                                        const isParent = moduleInfo.level === 0;
                                        const isExpanded = expandedRows.has(moduleKey);
                                        
                                        // Find parent to check if it's expanded (for child rows)
                                        let parentIsExpanded = true;
                                        if (!isParent) {
                                            for (let i = index - 1; i >= 0; i--) {
                                                if (APP_MODULES_HIERARCHICAL[i].level === 0) {
                                                    // FIX: Check parent expansion using parent's module key
                                                    parentIsExpanded = expandedRows.has(APP_MODULES_HIERARCHICAL[i].module);
                                                    break;
                                                }
                                            }
                                        }

                                        // Don't render children of collapsed parents
                                        if (!isParent && !parentIsExpanded) {
                                            return null;
                                        }

                                        const permissionData = permissions[moduleKey];
                                        if (!permissionData) return null;

                                        return (
                                            // FIX: Use moduleKey for key
                                            <tr key={moduleKey} className={`hover-highlight ${isParent ? 'parent-row' : ''}`} style={{ backgroundColor: isParent ? '#fff9c4' : undefined }}> 
                                                <td
                                                    style={{
                                                        paddingLeft: `${10 + (moduleInfo.level * 20)}px`,
                                                        fontWeight: isParent ? 'bold' : 'normal',
                                                        cursor: isParent ? 'pointer' : 'default'
                                                    }}
                                                    onClick={() => isParent && toggleRow(moduleKey)}
                                                >
                                                    {isParent && (
                                                        <i className={`fa ${isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'}`} style={{ marginRight: '8px', fontSize: '0.8em' }} />
                                                    )}
                                                    {displayTitle}
                                                </td>
                                                {['can_view', 'can_create', 'can_edit', 'can_delete'].map(perm => (
                                                    <td key={perm} style={{ textAlign: 'center' }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={permissionData[perm]}
                                                            onChange={e => handlePermissionChange(moduleKey, perm, e.target.checked)}
                                                            disabled={!hasPermission('DEPARTMENTS', 'can_edit')} 
                                                        />
                                                    </td>
                                                ))}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                    {/* 4. Consistent Save Button Styling */}
                    <div className="form-card-body" style={{ justifyContent: 'flex-end', marginTop: '20px', padding: '10px' }}>
                        {/* 5. Conditionally render the save button */}
                        {hasPermission('DEPARTMENTS', 'can_edit') && (
                            <button className="btn-primary" onClick={handleSave} disabled={saving || loading}>
                                {saving ? 'SAVING...' : 'SAVE PERMISSIONS'}
                            </button>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};


export default function Departments() {
  const { hasPermission } = usePermissions(); // 6. Get the permission checker function
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [form, setForm] = useState({ id: null, name: '', active: true });
  const [saving, setSaving] = useState(false);
    const { items: sortedRows, requestSort, getSortIndicator } = useSortableTable(rows);
  const [activeTab, setActiveTab] = useState('permissions');

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.append('search', search.trim());
      if (activeFilter !== '') params.append('active', activeFilter);
      const res = await fetch(`${API_BASE}?${params.toString()}`, { headers: authHeaders(false) });
      if (res.ok) {
        setRows(await res.json());
      }
    } finally {
      setLoading(false);
    }
  }, [search, activeFilter]);

  useEffect(() => { 
    // 7. Check for view permission before fetching data
    if (hasPermission('DEPARTMENTS', 'can_view')) {
        fetchRows(); 
    }
  }, [fetchRows, hasPermission]);

  const resetForm = () => setForm({ id: null, name: '', active: true });

  const editRow = r => {
    // 8. Permission check before editing
    if (!hasPermission('DEPARTMENTS', 'can_edit')) {
        toast.error("You do not have permission to edit departments.");
        return;
    }
    setForm({ id: r.id, name: r.name, active: r.active });
  }

  const save = async () => {
    if (!form.name.trim()) return;

    // 9. Permission check before saving
    const canSave = form.id ? hasPermission('DEPARTMENTS', 'can_edit') : hasPermission('DEPARTMENTS', 'can_create');
    if (!canSave) {
        toast.error("You do not have permission to save this department.");
        return;
    }

    setSaving(true);
    try {
      const payload = { name: form.name.trim(), active: form.active };
      const url = form.id ? `${API_BASE}${form.id}/` : API_BASE;
      const method = form.id ? 'PATCH' : 'POST';
      const res = await fetch(url, {
        method,
        headers: authHeaders(),
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        resetForm();
        fetchRows();
        toast.success('Department saved!');
      } else {
        const errorData = await res.json();
        toast.error(`Failed to save: ${JSON.stringify(errorData)}`);
      }
    } finally { setSaving(false); }
  };

  const remove = async id => {
    // 10. Permission check before deleting
    if (!hasPermission('DEPARTMENTS', 'can_delete')) {
        toast.error("You do not have permission to delete departments.");
        return;
    }
    if (!window.confirm('Delete department? This cannot be undone.')) return;
    try {
        await fetch(`${API_BASE}${id}/`, { method: 'DELETE', headers: authHeaders(false) });
        fetchRows();
        toast.success('Department deleted.');
    } catch (error) {
        toast.error(`Failed to delete: ${error.message}`);
    }
  };

  // 11. Top-level permission check for viewing the page
  if (!hasPermission('DEPARTMENTS', 'can_view')) {
    return (
        <div className="page-container">
            <div className="admin-header">
                <h2>Permission Denied</h2>
                <p>You do not have permission to view this page.</p>
            </div>
        </div>
    );
  }

  return (
    <div className="page-container">
      <div className="admin-header">
        <h2>Departments & Permissions</h2>
      </div>

      {/* --- FIX #2: Use the same CSS classes as PortReport.js for consistent styling --- */}
      <div className="custom-tabs-container" style={{ marginBottom: 16 }}>
        {/* 12. Conditionally render the Permissions tab */
        hasPermission('DEPARTMENTS', 'can_edit') && (
            <button className={`custom-tab ${activeTab === 'permissions' ? 'active' : ''}`} onClick={() => setActiveTab('permissions')}>
                Dept Permissions
            </button>
        )}
        <button className={`custom-tab ${activeTab === 'list' ? 'active' : ''}`} onClick={() => setActiveTab('list')}>
            Department List
        </button>
        
        {/* Payables Config Tab */
        (hasPermission('PAYABLES CONFIG', 'can_view') || hasPermission('ACCOUNTS CONFIG', 'can_view')) && (
            <button className={`custom-tab ${activeTab === 'payables_config' ? 'active' : ''}`} onClick={() => setActiveTab('payables_config')}>
                Payables Permissions
            </button>
        )}
      </div>

      <div className="tab-content">
        {activeTab === 'list' && (
            <>
                {/* 13. Conditionally render the Add/Edit form */
                (hasPermission('DEPARTMENTS', 'can_create') || hasPermission('DEPARTMENTS', 'can_edit')) && (
                    <div className="form-card">
                        <h4 className="form-card-title">{form.id ? 'Edit Department' : 'New Department'}</h4>
                        <div className="form-card-body">
                            <input
                                style={{ minWidth: 220 }}
                                placeholder="Department Name"
                                value={form.name}
                                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                                readOnly={form.id && !hasPermission('DEPARTMENTS', 'can_edit')}
                            />
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={form.active}
                                    onChange={e => setForm(f => ({ ...f, active: e.target.checked }))}
                                    disabled={form.id && !hasPermission('DEPARTMENTS', 'can_edit')}
                                />
                                Active
                            </label>
                            {/* Render save button only if user has create or edit permission */}
                            {( (form.id && hasPermission('DEPARTMENTS', 'can_edit')) || (!form.id && hasPermission('DEPARTMENTS', 'can_create')) ) && (
                                <button
                                    className="btn-primary"
                                    disabled={saving || !form.name.trim()}
                                    onClick={save}
                                >
                                    {saving ? 'Saving...' : 'Save'}
                                </button>
                            )}
                            {form.id && (
                                <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={resetForm}
                                >
                                    Cancel
                                </button>
                            )}
                        </div>
                    </div>
                )}
                <div className="table-responsive">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th onClick={() => requestSort('id')} style={{ cursor: 'pointer' }}>ID {getSortIndicator('id')}</th>
                                <th onClick={() => requestSort('name')} style={{ cursor: 'pointer' }}>NAME {getSortIndicator('name')}</th>
                                <th onClick={() => requestSort('active')} style={{ cursor: 'pointer' }}>STATUS {getSortIndicator('active')}</th>
                                <th onClick={() => requestSort('created_at')} style={{ cursor: 'pointer' }}>CREATED {getSortIndicator('created_at')}</th>
                                {/* 14. Conditionally render Actions column header */
                                (hasPermission('DEPARTMENTS', 'can_edit') || hasPermission('DEPARTMENTS', 'can_delete')) && (
                                    <th style={{ width: '100px' }}>ACTIONS</th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {loading && <tr><td colSpan={5} style={{ textAlign: 'center' }}>Loading...</td></tr>}
                            {!loading && rows.length === 0 && <tr><td colSpan={5}>No records found.</td></tr>}
                            {!loading && sortedRows.map(r => (
                            <tr key={r.id}>
                                <td>{r.id}</td>
                                <td>{r.name}</td>
                                <td><span className={`status-badge ${r.active ? 'status-active' : 'status-inactive'}`}>{r.active ? 'Active' : 'Inactive'}</span></td>
                                <td>{r.created_at?.slice(0,10)}</td>
                                {/* 15. Conditionally render the actions cell and buttons */}
                                {(hasPermission('DEPARTMENTS', 'can_edit') || hasPermission('DEPARTMENTS', 'can_delete')) && (
                                    <td className="actions">
                                        {hasPermission('DEPARTMENTS', 'can_edit') && (
                                            <button className="icon-btn" title="Edit" onClick={() => editRow(r)}>
                                                <i className="fa fa-pen" />
                                            </button>
                                        )}
                                        {hasPermission('DEPARTMENTS', 'can_delete') && (
                                            <button className="icon-btn danger" title="Delete" onClick={() => remove(r.id)}>
                                                <i className="fa fa-trash" />
                                            </button>
                                        )}
                                    </td>
                                )}
                            </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </>
        )}
        {activeTab === 'permissions' && hasPermission('DEPARTMENTS', 'can_edit') && (
            <PermissionsManager departments={rows} hasPermission={hasPermission} />
        )}
        {activeTab === 'payables_config' && (hasPermission('PAYABLES CONFIG', 'can_view') || hasPermission('ACCOUNTS CONFIG', 'can_view')) && (
            <PayablesConfigTab hasPermission={hasPermission} />
        )}
      </div>
    </div>
  );
}